import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { GitBranch, RefreshCw, TrendingUp, ShieldCheck, AlertTriangle, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import CalcInput from './CalcInput';
import { Input } from './ui/input';
import AuthDialog from './AuthDialog';

// Format ISO date as "Jun 15" or "Jun 15, 2026" (with year when != current)
const fmtDR = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
    const thisYear = new Date().getFullYear();
    const opts = d.getFullYear() !== thisYear
      ? { month: 'short', day: 'numeric', year: 'numeric' }
      : { month: 'short', day: 'numeric' };
    return d.toLocaleDateString('en-US', opts);
  } catch { return iso; }
};

/**
 * BranchCapitalCompareModal
 * Phase 1 — compare one product's capital across every branch and (admin/PIN/TOTP
 * gated) propagate the new PO capital (+ optional per-branch add-on) to the
 * branches you tick. Reuses AuthDialog (mode="either") for the PIN/TOTP gate.
 */
export default function BranchCapitalCompareModal({
  open, onOpenChange, product, sourceBranchId, sourcePoNumber, sourceDeliveryDate = '',
}) {
  const baseCapital = Number(product?.new_capital || 0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState({});       // branch_id -> bool
  const [addOns, setAddOns] = useState({});         // branch_id -> {type, value}
  const [globalType, setGlobalType] = useState('flat');
  const [globalValue, setGlobalValue] = useState('');
  const [authOpen, setAuthOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  // Server-side newer-DR block: forcing past it needs a written reason.
  const [blocked, setBlocked] = useState(null);
  const [forceReason, setForceReason] = useState('');

  const load = useCallback(async () => {
    if (!product?.product_id) return;
    setLoading(true);
    try {
      const res = await api.get('/branch-prices/branch-capital-compare', {
        params: { product_id: product.product_id, new_capital: baseCapital, source_branch_id: sourceBranchId || '' },
      });
      const list = (res.data.branches || []).filter(b => !b.is_source);
      setRows(list);
      setChecked({});
      setAddOns(Object.fromEntries(list.map(b => [b.branch_id, { type: 'flat', value: '' }])));
    } catch {
      toast.error('Failed to load branch capital');
    }
    setLoading(false);
  }, [product?.product_id, baseCapital, sourceBranchId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const newCapFor = useCallback((bid) => {
    const a = addOns[bid] || { type: 'flat', value: '' };
    const v = Number(a.value || 0);
    return a.type === 'percent' ? Math.round(baseCapital * (1 + v / 100) * 100) / 100 : Math.round((baseCapital + v) * 100) / 100;
  }, [addOns, baseCapital]);

  const applyGlobalAddOn = () => {
    setAddOns(prev => Object.fromEntries(rows.map(r => [r.branch_id, { type: globalType, value: globalValue }])));
    toast.success('Add-on applied to all branches');
  };

  const selectAllHigher = () => {
    const next = {};
    rows.forEach(r => { if (newCapFor(r.branch_id) > r.current_capital) next[r.branch_id] = true; });
    setChecked(next);
    toast.success(`Selected ${Object.keys(next).length} branch(es) where new > current`);
  };

  const selectedIds = useMemo(() => Object.keys(checked).filter(k => checked[k]), [checked]);

  const selectRegion = (list, region) => {
    const ids = list.map(r => r.branch_id);
    const allOn = ids.every(id => checked[id]);
    setChecked(prev => {
      const next = { ...prev };
      ids.forEach(id => { next[id] = !allOn; });
      return next;
    });
    if (!allOn) toast.success(`Selected ${ids.length} branch(es) in ${region || 'Unassigned'}`);
  };

  const doPropagate = async ({ force = false, reason = '' } = {}) => {
    setApplying(true);
    try {
      const targets = selectedIds.map(bid => ({
        branch_id: bid,
        add_on_type: (addOns[bid] || {}).type || 'flat',
        add_on_value: Number((addOns[bid] || {}).value || 0),
      }));
      const res = await api.post('/branch-prices/propagate-capital', {
        product_id: product.product_id,
        base_capital: baseCapital,
        source_branch_id: sourceBranchId || '',
        source_po_number: sourcePoNumber || '',
        source_delivery_date: sourceDeliveryDate || '',
        targets,
        ...(force ? { force_stale_dr: true, stale_dr_reason: reason } : {}),
      });
      toast.success(res.data.summary || 'Capital propagated');
      setBlocked(null);
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === 'stale_source_dr') {
        // Server refused: at least one target holds a newer DR. Offer the
        // reasoned override rather than a dead-end error toast.
        setForceReason('');
        setBlocked(d);
      } else {
        toast.error((typeof d === 'string' && d) || d?.message || 'Propagation failed');
      }
    }
    setApplying(false);
  };

  // Group rows by region for display
  const grouped = useMemo(() => {
    const m = new Map();
    rows.forEach(r => {
      const k = r.region || '';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    });
    return Array.from(m.entries());
  }, [rows]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl" data-testid="branch-capital-compare-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]" style={{ fontFamily: 'Manrope' }}>
              <GitBranch size={18} /> Compare &amp; Propagate Capital
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              <span className="font-semibold text-slate-700">{product?.name}</span> — new capital{' '}
              <span className="font-mono font-bold text-[#1A4D2E]">{formatPHP(baseCapital)}</span>
              {sourcePoNumber ? <> from <span className="font-mono">{sourcePoNumber}</span></> : null}
              {sourceDeliveryDate ? (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                  <CalendarClock size={10} /> Source DR: {fmtDR(sourceDeliveryDate)}
                </span>
              ) : (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-amber-600 bg-amber-50 rounded px-1.5 py-0.5">
                  <AlertTriangle size={10} /> No DR date on source
                </span>
              )}
              {' '}Tick the branches to apply this capital to (as a manual override; the next PO at each branch auto-resets it).
            </DialogDescription>
          </DialogHeader>

          {/* Global add-on + helpers */}
          <div className="flex flex-wrap items-end gap-2 rounded-lg border bg-slate-50/60 p-2.5" style={{ borderColor: 'var(--border-default)' }}>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold block mb-0.5">Add-on to all</label>
              <div className="flex items-center gap-1.5">
                <Select value={globalType} onValueChange={setGlobalType}>
                  <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">+ ₱ flat</SelectItem>
                    <SelectItem value="percent">+ % markup</SelectItem>
                  </SelectContent>
                </Select>
                <CalcInput value={globalValue} onChange={(v) => setGlobalValue(v)} placeholder="0.00" className="h-8 w-20 text-xs px-2" />
                <Button size="sm" variant="outline" className="h-8" onClick={applyGlobalAddOn} data-testid="apply-global-addon-btn">Apply to all</Button>
              </div>
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              {/* Conflict summary pill */}
              {sourceDeliveryDate && rows.filter(r => r.last_delivery_date && r.last_delivery_date > sourceDeliveryDate).length > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-1">
                  <AlertTriangle size={10} />
                  {rows.filter(r => r.last_delivery_date && r.last_delivery_date > sourceDeliveryDate).length} branch(es) with newer delivery
                </span>
              )}
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={selectAllHigher} data-testid="select-all-higher-btn">
                <TrendingUp size={13} className="mr-1" /> Select all where new &gt; current
              </Button>
            </div>
          </div>

          <ScrollArea className="h-[340px] -mx-1 px-1">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
                <RefreshCw size={15} className="animate-spin mr-2" /> Loading branches…
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">No other branches found.</div>
            ) : (
              grouped.map(([region, list]) => (
                <div key={region || 'none'} className="mb-2">
                  <div className="flex items-center justify-between px-1 py-1">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                      {region || 'Unassigned region'}
                    </div>
                    <Button size="sm" variant="ghost" className="h-6 text-[10px] text-[#1A4D2E] hover:bg-emerald-50 px-1.5"
                      onClick={() => selectRegion(list, region)}
                      data-testid={`select-region-${region || 'none'}`}>
                      <GitBranch size={11} className="mr-1" /> Select region
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {list.map(r => {
                      const nc = newCapFor(r.branch_id);
                      const higher = nc > r.current_capital;
                      const ao = addOns[r.branch_id] || { type: 'flat', value: '' };

                      // Conflict detection: target branch has a more recent delivery than source
                      const targetDR = r.last_delivery_date || '';
                      const conflict = targetDR && sourceDeliveryDate && targetDR > sourceDeliveryDate;
                      // Safe = source has DR date and it's newer (or equal)
                      const sourceSafe = sourceDeliveryDate && !conflict && targetDR;

                      return (
                        <div key={r.branch_id}
                          className={`rounded-lg border p-2 ${conflict ? 'border-amber-300 bg-amber-50/40' : checked[r.branch_id] ? 'border-[#1A4D2E] bg-emerald-50/40' : ''}`}
                          style={{ borderColor: conflict ? '' : checked[r.branch_id] ? '' : 'var(--border-default)' }}
                          data-testid={`compare-row-${r.branch_id}`}>

                          {/* Conflict warning banner */}
                          {conflict && (
                            <div className="flex items-start gap-1.5 mb-1.5 px-0.5">
                              <AlertTriangle size={11} className="text-amber-600 mt-0.5 shrink-0" />
                              <p className="text-[10px] text-amber-800 leading-tight">
                                <span className="font-semibold">Newer delivery at this branch</span> — their DR {fmtDR(targetDR)} is after your source DR {fmtDR(sourceDeliveryDate)}.
                                They may already have a higher capital. Propagating will overwrite it.
                              </p>
                            </div>
                          )}

                          <div className="flex items-center gap-2">
                            <Checkbox checked={!!checked[r.branch_id]}
                              onCheckedChange={(v) => setChecked(prev => ({ ...prev, [r.branch_id]: !!v }))}
                              data-testid={`compare-check-${r.branch_id}`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-800 truncate">{r.branch_name}</div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-[11px] text-slate-400">Stock {r.stock_on_hand} · {r.capital_method}</span>
                                {/* DR Date chip */}
                                {targetDR ? (
                                  <span className={`inline-flex items-center gap-1 text-[10px] rounded px-1 py-0.5 ${
                                    conflict
                                      ? 'bg-amber-100 text-amber-700 font-semibold'
                                      : sourceSafe
                                        ? 'bg-emerald-50 text-emerald-700'
                                        : 'bg-slate-100 text-slate-500'
                                  }`}
                                    title={`Last DR date at ${r.branch_name}`}>
                                    <CalendarClock size={9} />
                                    Last DR: {fmtDR(targetDR)}
                                    {conflict && ' ↑ newer'}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[10px] rounded px-1 py-0.5 bg-slate-50 text-slate-400"
                                    title="No DR date recorded at this branch yet">
                                    <CalendarClock size={9} /> No DR date
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right w-20">
                              <div className="text-[10px] text-slate-400">Current</div>
                              <div className="text-xs font-mono">{formatPHP(r.current_capital)}</div>
                            </div>
                            <div className="flex items-center gap-1 w-[150px]">
                              <Select value={ao.type} onValueChange={(t) => setAddOns(prev => ({ ...prev, [r.branch_id]: { ...ao, type: t } }))}>
                                <SelectTrigger className="h-7 w-16 text-[11px] px-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="flat">₱</SelectItem>
                                  <SelectItem value="percent">%</SelectItem>
                                </SelectContent>
                              </Select>
                              <CalcInput value={ao.value} onChange={(v) => setAddOns(prev => ({ ...prev, [r.branch_id]: { ...ao, value: v } }))}
                                placeholder="0" className="h-7 w-[60px] text-[11px] px-1.5" />
                            </div>
                            <div className="text-right w-20">
                              <div className="text-[10px] text-slate-400">New</div>
                              <div className={`text-xs font-mono font-bold ${higher ? 'text-red-600' : 'text-emerald-600'}`}>{formatPHP(nc)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </ScrollArea>

          <div className="flex items-center justify-between pt-1">
            <Badge variant="outline" className="text-xs">{selectedIds.length} selected</Badge>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="compare-cancel-btn">Cancel</Button>
              <Button className="bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                disabled={selectedIds.length === 0 || applying}
                onClick={() => setAuthOpen(true)}
                data-testid="modify-price-now-btn">
                <ShieldCheck size={14} className="mr-1.5" /> Modify price now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Newer-DR block — the server refused; forcing needs a written reason. */}
      <Dialog open={!!blocked} onOpenChange={(o) => { if (!o) setBlocked(null); }}>
        <DialogContent className="max-w-lg" data-testid="compare-stale-dr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle size={17} /> Newer delivery at these branches
            </DialogTitle>
            <DialogDescription>
              Their capital comes from a later DR than the receipt you are pushing,
              so it is more recent than this price. Overwriting moves their cost
              backwards.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-48 overflow-y-auto space-y-1">
            <div className="text-[11px] text-slate-500 flex items-center gap-1">
              <CalendarClock size={10} /> Your source DR: {fmtDR(blocked?.source_delivery_date) || 'unknown'}
            </div>
            {(blocked?.conflicts || []).map(c => (
              <div key={c.branch_id}
                className="rounded border border-amber-300 bg-amber-50/40 px-2 py-1 text-[11px] text-amber-800"
                data-testid={`compare-stale-row-${c.branch_id}`}>
                {c.branch_name} — their DR <b>{fmtDR(c.target_dr)}</b> is newer
              </div>
            ))}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-600">
              Reason for overwriting anyway
            </label>
            <Input value={forceReason} onChange={e => setForceReason(e.target.value)}
              placeholder="e.g. supplier reissued the DR with the correct price"
              className="h-8 text-sm" data-testid="compare-stale-reason" />
            <p className="text-[10px] text-slate-400">
              Recorded on each capital change and in the audit log.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setBlocked(null)} data-testid="compare-stale-cancel">
              Leave them alone
            </Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={applying || !forceReason.trim()}
              onClick={() => doPropagate({ force: true, reason: forceReason.trim() })}
              data-testid="compare-stale-force">
              <AlertTriangle size={14} className="mr-1.5" /> Overwrite anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin PIN / Google TOTP gate */}
      <AuthDialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        mode="either"
        context="cross_branch_capital_propagation"
        onVerified={() => { setAuthOpen(false); doPropagate(); }}
      />
    </>
  );
}
